import { ArrowUpRight, ChevronDown, MenuIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { lazy, Suspense } from 'react';
import { LanguageFlag, LANGUAGES } from './languages';
import { MOBILE_MENU_BUTTON, useNavLinks } from './nav';
import { SquiggleLine } from './primitives';

const MobileMenu = lazy(() => import('./mobile-menu'));

const XL_BREAKPOINT = 1280;

/**
 * A NATIVE `<select>` wearing the hand-off's trigger.
 *
 * The list has to be the platform's — that is what gives a phone its proper picker — but an `<option>`
 * may only contain text, so the flag could never live inside one. The designed trigger is therefore
 * rendered normally and the real control is stretched over it at zero opacity: the visitor sees the
 * flag and code, and gets the OS list on tap. `opacity-0` rather than `sr-only`, because a select that
 * is not laid out cannot be clicked.
 */
function LanguageSelector() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const current = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
	return (
		<div className="text-brand-nav relative flex items-center gap-1.5 rounded-full px-1 py-1">
			<LanguageFlag code={current.code} className="size-5 shrink-0" />
			<span className="font-medium">{current.short}</span>
			<ChevronDown className="size-4" />
			<select
				aria-label={t`Language`}
				value={current.code}
				onChange={(event) => {
					// Looked up rather than cast: `event.target.value` is a plain string, and the lookup is
					// what proves it is one of ours before it reaches `changeLanguage`.
					const next = LANGUAGES.find((lang) => lang.code === event.target.value);
					if (next) changeLanguage(next.code);
				}}
				className="absolute inset-0 cursor-pointer opacity-0"
			>
				{LANGUAGES.map((lang) => (
					<option key={lang.code} value={lang.code}>
						{lang.label}
					</option>
				))}
			</select>
		</div>
	);
}

export function Header() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const isMobile = useIsMobile(XL_BREAKPOINT);
	const navLinks = useNavLinks();

	return (
		<header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md">
			<div className="relative mx-auto flex h-[95px] max-w-[1440px] items-center px-6 xl:px-10">
				<Link to="/new" activeOptions={{ exact: true }} className="shrink-0">
					<span className="sr-only">{t`Worldcoo home`}</span>
					<WorldcooLogo className="h-11 w-auto" accentClassName="text-brand" />
				</Link>

				<nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 xl:flex">
					{navLinks.map((item) => (
						<Link key={item.to} to={item.to} className="group px-2 py-1 font-medium">
							{/* The current page is marked with the brand squiggle, not by recolouring the label.
							    The span is what the stroke is measured against, so it spans the word and not
							    the link's padding; hover previews the same stroke at half strength. */}
							<span className="relative inline-block">
								{item.label}
								<SquiggleLine className="opacity-0 transition-opacity group-hover:opacity-50 group-aria-[current=page]:opacity-100" />
							</span>
						</Link>
					))}
				</nav>

				<div className="ml-auto hidden items-center gap-4 xl:flex">
					<LanguageSelector />
					<Link
						to="/new/contact"
						className="bg-brand hover:bg-brand-strong flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 font-medium text-black transition-colors"
					>
						{t`Contact us`}
						<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>

				{isMobile && (
					<Suspense
						fallback={
							<button type="button" className={MOBILE_MENU_BUTTON} aria-label={t`Open navigation menu`}>
								<MenuIcon className="size-6" />
							</button>
						}
					>
						<MobileMenu language={language} changeLanguage={changeLanguage} />
					</Suspense>
				)}
			</div>
		</header>
	);
}
